"use client";

import { c as _c } from "react/compiler-runtime";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import EHRAppointmentRemindersCard from "@/src/components/cards/ehr-cards/ehr-appointment-reminders-card";
import EHRBookedAppointmentsCard from "@/src/components/cards/ehr-cards/ehr-booked-appointments-card";
import EHRDocumentCard from "@/src/components/cards/ehr-cards/ehr-document-card";
import LocationCard from "@/src/components/cards/location-card/location-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import PractitionerCard from "@/src/components/cards/practitioner-card/practitioner-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { useReferralLocationSwitchHandler, useReferralPractitionerSwitchHandler } from "@/src/hooks/use-entity-switch-handlers";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
export interface AthenaReferralCardsData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}
interface AthenaReferralCardsProps {
  referral: Referral;
  athenaData: AthenaReferralCardsData | null;
}
export function AthenaReferralCards(t0) {
  const $ = _c(59);
  const {
    referral,
    athenaData
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referral.id);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = eligibilityCheck ? resolveStediEligibilityDisplay(eligibilityCheck).eligibilityStatus : undefined;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const status = t1;
  const eligibilityStatus = status === "active" || status === "inactive" ? status : undefined;
  const {
    createLocationSwitchHandler,
    isPending: isLocationPending
  } = useReferralLocationSwitchHandler();
  const {
    createPractitionerSwitchHandler,
    isPending: isPractitionerPending
  } = useReferralPractitionerSwitchHandler();
  const primaryCoverage = referral.primaryCoverage;
  const secondaryCoverage = referral.secondaryCoverage;
  let primaryEhrCoverage;
  let t2;
  if ($[2] !== athenaData?.coverages) {
    const ehrCoverages = [...(athenaData?.coverages || [])].sort(_temp);
    primaryEhrCoverage = ehrCoverages.find(_temp2) ?? ehrCoverages[0] ?? null;
    t2 = ehrCoverages.find(_temp3) ?? ehrCoverages[1] ?? null;
    $[2] = athenaData?.coverages;
    $[3] = primaryEhrCoverage;
    $[4] = t2;
  } else {
    primaryEhrCoverage = $[3];
    t2 = $[4];
  }
  const secondaryEhrCoverage = t2;
  const t3 = athenaData?.document || null;
  const t4 = athenaData?.expectedDocument || null;
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <EHRDocumentCard document={t3} expectedDocument={t4} />;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== athenaData?.careTeam || $[9] !== createPractitionerSwitchHandler || $[10] !== isPractitionerPending || $[11] !== referral) {
    t6 = referral.referringProvider && <PractitionerCard practitioner={referral.referringProvider} type="Referring" compareMode={true} secondaryPractitioners={athenaData?.careTeam || null} onEditPractitioner={createPractitionerSwitchHandler(referral, "referringProvider")} onEditPractitionerLoading={isPractitionerPending} isEditable={true} />;
    $[8] = athenaData?.careTeam;
    $[9] = createPractitionerSwitchHandler;
    $[10] = isPractitionerPending;
    $[11] = referral;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== createLocationSwitchHandler || $[14] !== isLocationPending || $[15] !== referral) {
    t7 = referral.referringLocation && <LocationCard location={referral.referringLocation} type="Referring" onEditLocation={createLocationSwitchHandler(referral, "referringLocation")} onEditLocationLoading={isLocationPending} isEditable={true} />;
    $[13] = createLocationSwitchHandler;
    $[14] = isLocationPending;
    $[15] = referral;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = athenaData?.patientDetails;
  let t9;
  if ($[17] !== referral.patient || $[18] !== t8) {
    t9 = <PatientCard patient={referral.patient} compareMode={true} secondaryPatient={t8} />;
    $[17] = referral.patient;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== eligibilityStatus || $[21] !== primaryCoverage || $[22] !== primaryEhrCoverage) {
    t10 = primaryCoverage && <CoverageCard key="coverage-primary" coverage={primaryCoverage} type="Primary" compareMode={true} secondaryCoverage={primaryEhrCoverage} isEditable={true} eligibilityStatus={eligibilityStatus} />;
    $[20] = eligibilityStatus;
    $[21] = primaryCoverage;
    $[22] = primaryEhrCoverage;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== secondaryCoverage || $[25] !== secondaryEhrCoverage) {
    t11 = secondaryCoverage && <CoverageCard key="coverage-secondary" coverage={secondaryCoverage} type="Secondary" compareMode={true} secondaryCoverage={secondaryEhrCoverage} isEditable={true} />;
    $[24] = secondaryCoverage;
    $[25] = secondaryEhrCoverage;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <div className="space-y-8">{t10}{t11}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== athenaData?.appointmentReminders) {
    t13 = athenaData?.appointmentReminders || [];
    $[30] = athenaData?.appointmentReminders;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <EHRAppointmentRemindersCard appointmentReminders={t13} />;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== athenaData?.bookedAppointments) {
    t15 = athenaData?.bookedAppointments || [];
    $[34] = athenaData?.bookedAppointments;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <EHRBookedAppointmentsCard bookedAppointments={t15} />;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== createPractitionerSwitchHandler || $[39] !== isPractitionerPending || $[40] !== referral) {
    t17 = referral.receivingProvider && <PractitionerCard practitioner={referral.receivingProvider} type="Receiving" compareMode={false} onEditPractitioner={createPractitionerSwitchHandler(referral, "receivingProvider")} onEditPractitionerLoading={isPractitionerPending} isEditable={true} />;
    $[38] = createPractitionerSwitchHandler;
    $[39] = isPractitionerPending;
    $[40] = referral;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== createLocationSwitchHandler || $[43] !== isLocationPending || $[44] !== referral) {
    t18 = referral.receivingLocation && <LocationCard location={referral.receivingLocation} type="Receiving" onEditLocation={createLocationSwitchHandler(referral, "receivingLocation")} onEditLocationLoading={isLocationPending} isEditable={true} />;
    $[42] = createLocationSwitchHandler;
    $[43] = isLocationPending;
    $[44] = referral;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== referral) {
    t19 = <ReferralCard referral={referral} isEditable={true} />;
    $[46] = referral;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t12 || $[49] !== t14 || $[50] !== t16 || $[51] !== t17 || $[52] !== t18 || $[53] !== t19 || $[54] !== t5 || $[55] !== t6 || $[56] !== t7 || $[57] !== t9) {
    t20 = <>{t5}{t6}{t7}{t9}{t12}{t14}{t16}{t17}{t18}{t19}</>;
    $[48] = t12;
    $[49] = t14;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
    $[54] = t5;
    $[55] = t6;
    $[56] = t7;
    $[57] = t9;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  return t20;
}
function _temp3(cov_0) {
  return cov_0.order === 2;
}
function _temp2(cov) {
  return cov.order === 1;
}
function _temp(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}